import type { DependencyState, TerminalSchema } from "@wishlink/applens-core";
export interface HomeDependencies {
  "app.dep.creator_data": DependencyState;
  "app.dep.creator_config": DependencyState;
}
const dependencyRule = {
  type: "enum",
  values: ["network", "inflight", "cache", "absent"],
  default: "absent",
} as const;
export const HOME_TERMINAL_SCHEMA: TerminalSchema = {
  "app.dep.creator_data": dependencyRule,
  "app.dep.creator_config": dependencyRule,
};

export interface QueryTiming {
  startedTimeStamp?: number;
  fulfilledTimeStamp?: number;
  status?: string;
  hasData?: boolean;
}
export type DependencyTimings = {
  creator_data?: QueryTiming;
  creator_config?: QueryTiming;
};
export function queryTiming(
  query: QueryTiming & { data?: unknown }
): QueryTiming {
  return {
    startedTimeStamp: query.startedTimeStamp,
    fulfilledTimeStamp: query.fulfilledTimeStamp,
    status: query.status,
    hasData: query.data !== undefined,
  };
}

/** RTK uses Date.now. The caller anchors the core start to the injected epoch clock once. */
export function dependencyState(
  query: QueryTiming | undefined,
  homeEpoch: number,
  initial = false
): DependencyState {
  const started = query?.startedTimeStamp;
  const fulfilled = query?.fulfilledTimeStamp;
  if (
    !Number.isFinite(homeEpoch) ||
    started === undefined ||
    !Number.isFinite(started)
  )
    return "absent";
  // The initial snapshot proves a request already existed, including same-ms ties.
  if (!initial && started >= homeEpoch) return "network";
  if (
    query?.status === "pending" ||
    (fulfilled !== undefined && fulfilled >= started && fulfilled > homeEpoch)
  )
    return "inflight";
  // A rejected refetch advances startedTimeStamp but keeps the last successful
  // data. Carry only its presence, never the payload, into dependency snapshots.
  return fulfilled !== undefined &&
    Number.isFinite(fulfilled) &&
    (fulfilled >= started || query?.hasData === true)
    ? "cache"
    : "absent";
}

export function createDependencies(
  homeEpoch: number,
  initial: DependencyTimings
) {
  const fields = ["creator_data", "creator_config"] as const;
  const values: HomeDependencies = {
    "app.dep.creator_data": dependencyState(
      initial.creator_data,
      homeEpoch,
      true
    ),
    "app.dep.creator_config": dependencyState(
      initial.creator_config,
      homeEpoch,
      true
    ),
  };
  const rank = { absent: 0, cache: 1, inflight: 2, network: 3 };
  return (latest: DependencyTimings): HomeDependencies => {
    for (const field of fields) {
      // An unchanged initial request cannot become 'network' at a same-ms boundary.
      const unchanged =
        initial[field]?.startedTimeStamp !== undefined &&
        initial[field]?.startedTimeStamp === latest[field]?.startedTimeStamp;
      const next = dependencyState(latest[field], homeEpoch, unchanged);
      const key = `app.dep.${field}` as keyof HomeDependencies;
      if (rank[next] > rank[values[key]]) values[key] = next;
    }
    return { ...values };
  };
}
